/*
 * PaceCerto — núcleo do navegador: catálogo em JSON, formatações,
 * estado da comparação (até 3 modelos, localStorage), botão "Comparar"
 * e barra fixa global. Portado de compare-context.tsx / BarraComparar.tsx.
 */
(() => {
  if (window.PaceCerto) return;

  const cfg = window.PaceCertoConfig || {};
  const LIMITE = 3;
  const CHAVE = 'pacecerto:comparar';
  const CHAVE_CATALOGO = 'pacecerto:catalogo:v2';
  const VALIDADE_CATALOGO = 10 * 60 * 1000;

  /* ------------------------------------------------------------ formatos */

  const moeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  function formatMoeda(valor) {
    return typeof valor === 'number' && Number.isFinite(valor) ? moeda.format(valor) : '—';
  }

  /** "4:50" → 4.8333 (mesma regra de paraRitmo em mappers.ts) */
  function paraRitmo(valor) {
    if (valor === null || valor === undefined) return null;
    const texto = String(valor).trim();
    if (texto === '') return null;
    const m = texto.match(/^(\d{1,2})\s*[:.,]\s*(\d{1,2})$/);
    if (m) return Number(m[1]) + Number(m[2]) / 60;
    const simples = Number(texto);
    return Number.isFinite(simples) ? simples : null;
  }

  /** 4.5 → "4:30" */
  function formatRitmo(minutos) {
    if (minutos === null || minutos === undefined || !Number.isFinite(minutos)) return '—';
    let min = Math.floor(minutos);
    let seg = Math.round((minutos - min) * 60);
    if (seg === 60) {
      min += 1;
      seg = 0;
    }
    return `${min}:${String(seg).padStart(2, '0')}`;
  }

  function paraNumero(valor) {
    if (valor === null || valor === undefined || valor === '') return null;
    const n = Number(valor);
    return Number.isFinite(n) ? n : null;
  }

  const escapar = (texto) =>
    String(texto ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  /* ------------------------------------------------------------ catálogo */

  function normalizar(bruto) {
    const s = bruto.specs || {};
    return {
      ...bruto,
      preco: paraNumero(bruto.preco) ?? 0,
      precoDe: paraNumero(bruto.precoDe),
      cores: Array.isArray(bruto.cores) ? bruto.cores : [],
      numeracoes: Array.isArray(bruto.numeracoes) ? bruto.numeracoes : [],
      numeracoesTodas: Array.isArray(bruto.numeracoesTodas) ? bruto.numeracoesTodas : [],
      specs: {
        ritmo_min: paraRitmo(s.ritmo_min),
        ritmo_max: paraRitmo(s.ritmo_max),
        drop: paraNumero(s.drop),
        peso_g: paraNumero(s.peso_g),
        stack_calcanhar: paraNumero(s.stack_calcanhar),
        stack_antepe: paraNumero(s.stack_antepe),
        vida_util_km: paraNumero(s.vida_util_km),
        placa: s.placa || null,
        espuma: s.espuma || null,
        pisada: s.pisada || null,
        nivel: s.nivel || null,
        uso: s.uso || null,
        origem: s.origem || null,
      },
    };
  }

  let promessaCatalogo = null;
  function catalogo() {
    if (promessaCatalogo) return promessaCatalogo;
    promessaCatalogo = (async () => {
      try {
        const salvo = JSON.parse(sessionStorage.getItem(CHAVE_CATALOGO) || 'null');
        if (salvo && Date.now() - salvo.t < VALIDADE_CATALOGO && Array.isArray(salvo.produtos) && salvo.produtos.length > 0) {
          return salvo.produtos.map(normalizar);
        }
      } catch (e) {
        /* sem cache */
      }
      // sem cabeçalho "Accept: application/json": com ele a Shopify devolve o JSON padrão da coleção, não o template
      const resposta = await fetch(cfg.catalogo || '/collections/todos?view=pc-catalogo', { credentials: 'same-origin' });
      if (!resposta.ok) throw new Error(`Catálogo indisponível (${resposta.status})`);
      const dados = await resposta.json();
      if (!Array.isArray(dados.produtos)) throw new Error('Catálogo em formato inesperado');
      const produtos = dados.produtos;
      try {
        sessionStorage.setItem(CHAVE_CATALOGO, JSON.stringify({ t: Date.now(), produtos }));
      } catch (e) {
        /* cache é opcional */
      }
      return produtos.map(normalizar);
    })();
    promessaCatalogo.catch(() => {
      promessaCatalogo = null;
    });
    return promessaCatalogo;
  }

  /* ---------------------------------------------------------- comparação */

  function handlesDeParametro(parametro) {
    if (!parametro) return [];
    const lista = String(parametro)
      .split('--')
      .map((h) => h.trim().toLowerCase())
      .filter((h) => h.length > 0);
    return [...new Set(lista)].slice(0, LIMITE);
  }

  function parametroDeHandles(handles) {
    return [...new Set(handles)].slice(0, LIMITE).join('--');
  }

  function ler() {
    try {
      const bruto = JSON.parse(localStorage.getItem(CHAVE) || '[]');
      if (!Array.isArray(bruto)) return [];
      return [...new Set(bruto.map(String))].slice(0, LIMITE);
    } catch (e) {
      return [];
    }
  }

  let atuais = ler();

  function gravar(proximos) {
    const limpos = [...new Set(proximos.filter(Boolean))].slice(0, LIMITE);
    const igual = limpos.length === atuais.length && limpos.every((h, i) => h === atuais[i]);
    if (igual) return;
    atuais = limpos;
    try {
      localStorage.setItem(CHAVE, JSON.stringify(atuais));
    } catch (e) {
      /* persistência é opcional */
    }
    document.dispatchEvent(new CustomEvent('pc:comparar', { detail: { handles: [...atuais] } }));
  }

  window.addEventListener('storage', (evento) => {
    if (evento.key !== CHAVE) return;
    atuais = ler();
    document.dispatchEvent(new CustomEvent('pc:comparar', { detail: { handles: [...atuais] } }));
  });

  const comparar = {
    limite: LIMITE,
    handles: () => [...atuais],
    cheio: () => atuais.length >= LIMITE,
    tem: (h) => atuais.includes(h),
    definir: (lista) => gravar(lista),
    adicionar: (h) => {
      if (!atuais.includes(h) && atuais.length < LIMITE) gravar([...atuais, h]);
    },
    remover: (h) => gravar(atuais.filter((x) => x !== h)),
    alternar: (h) => {
      if (atuais.includes(h)) gravar(atuais.filter((x) => x !== h));
      else if (atuais.length < LIMITE) gravar([...atuais, h]);
    },
    limpar: () => gravar([]),
    link: (handles) => {
      const base = cfg.comparar || '/pages/comparar';
      const lista = handles ?? atuais;
      return lista.length > 0 ? `${base}?modelos=${encodeURIComponent(parametroDeHandles(lista))}` : base;
    },
  };

  /* ------------------------------------------------------------- avisos */

  let regiaoAviso = null;
  function avisar(texto) {
    if (!regiaoAviso) {
      regiaoAviso = document.createElement('div');
      regiaoAviso.className = 'pc-visually-hidden';
      regiaoAviso.setAttribute('role', 'status');
      regiaoAviso.setAttribute('aria-live', 'polite');
      document.body.appendChild(regiaoAviso);
    }
    regiaoAviso.textContent = '';
    window.setTimeout(() => {
      regiaoAviso.textContent = texto;
    }, 50);
  }

  /* ------------------------------------------------------ régua de ritmo */

  const INICIO = 3;
  const FIM = 7;
  const posicao = (v) => Math.min(100, Math.max(0, ((v - INICIO) / (FIM - INICIO)) * 100));

  function reguaHTML(min, max, opcoes = {}) {
    if (min === null || max === null || min === undefined || max === undefined) {
      return `<p class="pc-regua pc-regua--vazia">Ritmo não informado</p>`;
    }
    const a = Math.min(min, max);
    const b = Math.max(min, max);
    const x = posicao(a);
    const largura = Math.max(2, posicao(b) - x);
    const marcas = [3, 4, 5, 6, 7]
      .map((m) => `<span style="left:${posicao(m)}%">${m}:00</span>`)
      .join('');
    return `
      <div class="pc-regua${opcoes.escura ? ' pc-regua--escura' : ''}" role="img" aria-label="Faixa de ritmo: de ${formatRitmo(a)} a ${formatRitmo(b)} minutos por quilômetro">
        <div class="pc-regua__trilho" aria-hidden="true"><span class="pc-regua__faixa" style="left:${x}%;width:${largura}%"></span></div>
        <div class="pc-regua__marcas" aria-hidden="true">${marcas}</div>
        <p class="pc-regua__valores" aria-hidden="true"><strong>${formatRitmo(a)}–${formatRitmo(b)}</strong> /km</p>
        ${opcoes.estimado ? '<p class="pc-regua__nota">Faixa estimada: o fabricante não publica ritmo.</p>' : ''}
      </div>`;
  }

  /* ------------------------------------------------- botão "Comparar" */

  // ícone "git-compare" (Lucide, ISC), o mesmo do card no Lovable
  const ICONE = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/><path d="M13 6h3a2 2 0 0 1 2 2v7"/><path d="M11 18H8a2 2 0 0 1-2-2V9"/></svg>`;
  const ICONE_OK = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg>`;

  class BotaoComparar extends HTMLElement {
    connectedCallback() {
      this.handle = this.dataset.handle;
      this.titulo = this.dataset.titulo || this.handle;
      this.modo = this.dataset.modo || 'icone';
      if (!this.handle) return;
      if (!this.botao) {
        this.botao = document.createElement('button');
        this.botao.type = 'button';
        this.botao.className = `pc-comparar-botao pc-comparar-botao--${this.modo}`;
        this.appendChild(this.botao);
        this.botao.addEventListener('click', (evento) => {
          evento.preventDefault();
          evento.stopPropagation();
          const estava = comparar.tem(this.handle);
          if (!estava && comparar.cheio()) {
            avisar(`A comparação aceita no máximo ${LIMITE} modelos.`);
            return;
          }
          comparar.alternar(this.handle);
          avisar(estava ? `${this.titulo} saiu da comparação.` : `${this.titulo} entrou na comparação.`);
        });
      }
      this.aoMudar = () => this.render();
      document.addEventListener('pc:comparar', this.aoMudar);
      this.render();
    }
    disconnectedCallback() {
      document.removeEventListener('pc:comparar', this.aoMudar);
    }
    render() {
      const marcado = comparar.tem(this.handle);
      const bloqueado = !marcado && comparar.cheio();
      this.botao.setAttribute('aria-pressed', String(marcado));
      this.botao.disabled = bloqueado;
      this.botao.title = bloqueado ? `A comparação aceita no máximo ${LIMITE} modelos.` : '';
      if (this.modo === 'texto') {
        const rotulo = this.dataset.rotulo || 'Adicionar à comparação';
        const ativo = this.dataset.rotuloAtivo || 'Na comparação';
        this.botao.innerHTML = `${marcado ? ICONE_OK : ICONE}<span>${escapar(marcado ? ativo : rotulo)}</span>`;
      } else {
        this.botao.innerHTML = `${ICONE}<span class="pc-visually-hidden">${marcado ? 'Remover' : 'Adicionar'} ${escapar(this.titulo)} ${marcado ? 'da' : 'à'} comparação</span>`;
      }
    }
  }
  if (!customElements.get('pc-botao-comparar')) customElements.define('pc-botao-comparar', BotaoComparar);

  /* ------------------------------------------------------ barra global */

  /** Na tela de comparação (com modelos) o cabeçalho próprio substitui a barra no celular. */
  function naPaginaComparar() {
    const base = (cfg.comparar || '/pages/comparar').replace(/\/$/, '');
    const naPagina = window.location.pathname.replace(/\/$/, '') === base;
    return naPagina && new URLSearchParams(window.location.search).has('modelos');
  }

  function medirBarra() {
    if (!barra || barra.hidden) return;
    document.documentElement.style.setProperty('--pc-barra-altura', `${Math.ceil(barra.getBoundingClientRect().height)}px`);
  }

  let barra = null;
  async function renderBarra() {
    const handles = comparar.handles();
    if (handles.length === 0) {
      if (barra) barra.hidden = true;
      document.documentElement.classList.remove('pc-com-barra');
      return;
    }
    if (!barra) {
      barra = document.createElement('div');
      barra.className = 'pc-barra-comparar';
      barra.setAttribute('data-palco', 'escuro');
      barra.setAttribute('role', 'region');
      barra.setAttribute('aria-label', 'Modelos na comparação');
      document.body.appendChild(barra);
      if ('ResizeObserver' in window) new ResizeObserver(medirBarra).observe(barra);
      barra.addEventListener('click', (evento) => {
        const remover = evento.target.closest('[data-remover]');
        if (remover) {
          comparar.remover(remover.dataset.remover);
          return;
        }
        if (evento.target.closest('[data-limpar]')) comparar.limpar();
      });
    }
    barra.hidden = false;
    barra.classList.toggle('pc-barra-comparar--pagina', naPaginaComparar());
    document.documentElement.classList.add('pc-com-barra');

    let produtos = [];
    try {
      produtos = await catalogo();
    } catch (e) {
      produtos = [];
    }
    if (comparar.handles().join() !== handles.join()) return; // mudou enquanto carregava
    const itens = handles
      .map((h) => {
        const p = produtos.find((x) => x.handle === h);
        const titulo = p ? p.titulo : h;
        return `
          <li class="pc-barra-comparar__item">
            ${p && p.imagem ? `<img src="${escapar(p.imagem)}" alt="" width="48" height="36" loading="lazy">` : ''}
            <span class="pc-barra-comparar__nome">${escapar(titulo)}</span>
            <button type="button" class="pc-barra-comparar__remover" data-remover="${escapar(h)}" aria-label="Remover ${escapar(titulo)} da comparação">
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="m3 3 8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.8"/></svg>
            </button>
          </li>`;
      })
      .join('');
    barra.innerHTML = `
      <div class="pc-container pc-barra-comparar__grade">
        <span class="pc-barra-comparar__rotulo">Comparar</span>
        <ul class="pc-barra-comparar__lista" role="list">${itens}</ul>
        <button type="button" class="pc-barra-comparar__limpar" data-limpar>Limpar</button>
        <a class="pc-button pc-barra-comparar__ir" href="${escapar(comparar.link(handles))}">Comparar (${handles.length})</a>
      </div>`;
    medirBarra();
  }

  const semAcento = (t) =>
    String(t ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  const slug = (t) =>
    semAcento(t)
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  const nomeCurto = (titulo) => String(titulo ?? '').split(/\s+/).slice(0, 3).join(' ');

  document.addEventListener('pc:comparar', renderBarra);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', renderBarra);
  else renderBarra();

  window.addEventListener('popstate', renderBarra);

  /* ------------------------------------------------ vistos recentemente */

  const CHAVE_VISTOS = 'pacecerto.vistos';
  const LIMITE_VISTOS = 12;

  function lerVistos() {
    try {
      const bruto = JSON.parse(localStorage.getItem(CHAVE_VISTOS) || '[]');
      return Array.isArray(bruto) ? bruto.filter((h) => typeof h === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  function registrarVisto(handle) {
    if (!handle) return;
    try {
      const lista = [handle, ...lerVistos().filter((h) => h !== handle)].slice(0, LIMITE_VISTOS);
      localStorage.setItem(CHAVE_VISTOS, JSON.stringify(lista));
    } catch (e) {
      /* armazenamento opcional */
    }
  }

  function miniCardHTML(p) {
    const chave = slug(p.categoria);
    const cats = ['competicao', 'treino-rapido', 'rodagem', 'longao', 'estabilidade', 'trilha'];
    const temRitmo = Number.isFinite(p.specs.ritmo_min) && Number.isFinite(p.specs.ritmo_max);
    const url = escapar(p.url);
    return `
      <li class="pc-mini-card">
        <a class="pc-mini-card__link" href="${url}" aria-label="${escapar(p.titulo)}"></a>
        <div class="pc-mini-card__foto">
          ${p.imagem ? `<img src="${escapar(p.imagem)}" alt="" width="480" height="360" loading="lazy">` : ''}
          ${p.precoDe && p.precoDe > p.preco ? '<span class="pc-mini-card__promo">Promoção</span>' : ''}
          <pc-botao-comparar class="pc-card-comparar" data-handle="${escapar(p.handle)}" data-titulo="${escapar(p.titulo)}" data-modo="icone"></pc-botao-comparar>
        </div>
        <div class="pc-mini-card__corpo">
          <div class="pc-marca-categoria">
            ${p.marca ? `<span class="pc-marca">${escapar(p.marca)}</span>` : ''}
            ${cats.includes(chave) ? `<span class="pc-badge" data-pc-category="${chave}">${escapar(p.categoria)}</span>` : ''}
            ${p.cores.length > 1 ? `<span class="pc-marca-categoria__cores">${p.cores.length} cores</span>` : ''}
          </div>
          <h3 class="pc-mini-card__titulo">${escapar(p.titulo)}</h3>
          <p class="pc-mini-card__preco">
            <strong>${formatMoeda(p.preco)}</strong>
            ${p.precoDe && p.precoDe > p.preco ? `<del>${formatMoeda(p.precoDe)}</del>` : ''}
          </p>
          ${
            temRitmo
              ? `<p class="pc-card-ritmo"><span class="pc-card-ritmo__rotulo">Ritmo</span><span class="pc-card-ritmo__valor">${formatRitmo(p.specs.ritmo_min)}–${formatRitmo(p.specs.ritmo_max)}</span><span class="pc-card-ritmo__unidade">/km</span></p>`
              : ''
          }
        </div>
      </li>`;
  }

  async function renderVistos(secao) {
    const atual = secao.dataset.handle || '';
    const limite = Number(secao.dataset.limite) || 4;
    if (atual && secao.dataset.registrar !== 'false') registrarVisto(atual);
    const handles = lerVistos().filter((h) => h !== atual);
    if (handles.length === 0) return;
    let produtos = [];
    try {
      produtos = await catalogo();
    } catch (e) {
      return;
    }
    const vistos = handles
      .map((h) => produtos.find((p) => p.handle === h))
      .filter((p) => p && p.disponivel !== false)
      .slice(0, limite);
    if (vistos.length === 0) return;
    const grade = secao.querySelector('[data-pc-vistos-grade]');
    if (!grade) return;
    grade.innerHTML = vistos.map(miniCardHTML).join('');
    secao.hidden = false;
  }

  function iniciarVistos() {
    document.querySelectorAll('[data-pc-vistos]').forEach((secao) => {
      if (secao.dataset.pcIniciado) return;
      secao.dataset.pcIniciado = '1';
      renderVistos(secao);
    });
  }

  /* ------------------------------ comprar rápido e numeração obrigatória */

  // Mesmo comportamento do Lovable (produtos.$handle.tsx e QuickAdd.tsx):
  // - nenhuma numeração vem marcada por padrão; sem escolha, o carrinho não recebe o item;
  // - a última numeração comprada fica salva por marca e já vem marcada no próximo modelo da mesma marca.
  const CHAVE_NUMERO = 'pacecerto.numeracao';
  const escolhas = new Map(); // escopo → numeração escolhida
  const semeados = new WeakSet();

  function lerNumeroSalvo() {
    try {
      const salvo = JSON.parse(localStorage.getItem(CHAVE_NUMERO) || 'null');
      return salvo && typeof salvo.numeracao === 'string' ? salvo : null;
    } catch (e) {
      return null;
    }
  }

  const campoNumeracao = (escopo) => escopo && escopo.querySelector('fieldset.variant-option--pc-numeracao');
  const marcadaEm = (campo) => campo && campo.querySelector('input[type="radio"]:checked');
  const marcaDo = (escopo) =>
    (campoNumeracao(escopo)?.dataset.pcMarca || escopo.querySelector('.pc-marca')?.textContent || '').trim();

  function chaveEscopo(escopo) {
    const vp = escopo.querySelector('variant-picker');
    const produto = vp?.dataset.productId || '';
    return `${noModal(escopo) ? 'modal' : 'pagina'}:${produto}`;
  }

  const noModal = (elemento) => Boolean(elemento.closest('.quick-add-modal'));

  function escopoPrincipal() {
    return [...document.querySelectorAll('product-component')].find((e) => !noModal(e) && campoNumeracao(e)) || null;
  }

  function escopoDe(elemento) {
    if (elemento.closest('sticky-add-to-cart')) return escopoPrincipal();
    return elemento.closest('product-component');
  }

  function mensagemNumero(campo, mostrar) {
    const ajuda = campo.parentElement?.querySelector('.pc-num__ajuda');
    let aviso = campo.parentElement?.querySelector('.pc-num__obrigatorio');
    if (!mostrar) {
      aviso?.remove();
      campo.classList.remove('pc-num--erro');
      return;
    }
    if (!aviso) {
      aviso = document.createElement('p');
      aviso.className = 'pc-num__obrigatorio';
      aviso.setAttribute('role', 'alert');
      aviso.textContent = 'Escolha uma numeração disponível para adicionar ao carrinho.';
      (ajuda || campo).insertAdjacentElement('afterend', aviso);
    }
    campo.classList.add('pc-num--erro');
  }

  function pendente(escopo) {
    const campo = campoNumeracao(escopo);
    if (!campo) return false;
    const disponiveis = campo.querySelectorAll('input[data-option-available="true"]');
    if (disponiveis.length === 0) return false;
    const marcada = marcadaEm(campo);
    return !marcada || escolhas.get(chaveEscopo(escopo)) !== marcada.value;
  }

  function semear(escopo) {
    const campo = campoNumeracao(escopo);
    if (!campo || semeados.has(escopo)) return;
    semeados.add(escopo);
    const chave = chaveEscopo(escopo);
    if (escolhas.has(chave)) return;
    const marcada = marcadaEm(campo);
    // quem chega com ?variant= (anúncio, link compartilhado) já escolheu
    if (escopo === escopoPrincipal() && new URLSearchParams(location.search).has('variant') && marcada) {
      escolhas.set(chave, marcada.value);
      return;
    }
    const salvo = lerNumeroSalvo();
    if (!salvo || salvo.marca !== marcaDo(escopo)) return;
    const alvo = [...campo.querySelectorAll('input[type="radio"]')].find(
      (i) => i.value === salvo.numeracao && i.dataset.optionAvailable === 'true',
    );
    if (!alvo) return;
    escolhas.set(chave, alvo.value);
    if (!alvo.checked) alvo.click();
  }

  function atualizarNumeracao() {
    document.querySelectorAll('product-component').forEach((escopo) => {
      const campo = campoNumeracao(escopo);
      if (!campo) return;
      semear(escopo);
      const esperando = pendente(escopo);
      campo.classList.toggle('pc-num--pendente', esperando);
      if (!esperando) mensagemNumero(campo, false);
    });
  }

  let agendado = 0;
  function agendarNumeracao() {
    if (agendado) return;
    // setTimeout (e não requestAnimationFrame): continua rodando com a aba em segundo plano
    agendado = window.setTimeout(() => {
      agendado = 0;
      atualizarNumeracao();
      iniciarVistos();
    }, 30);
  }

  document.addEventListener(
    'change',
    (evento) => {
      const input = evento.target;
      if (!(input instanceof HTMLInputElement) || !input.closest('fieldset.variant-option--pc-numeracao')) return;
      const escopo = input.closest('product-component');
      if (escopo) escolhas.set(chaveEscopo(escopo), input.value);
      agendarNumeracao();
    },
    true,
  );

  function barrarSemNumero(evento) {
    const alvo = evento.target instanceof Element ? evento.target : null;
    if (!alvo) return;
    const gatilho = alvo.closest(
      'button[name="add"], .add-to-cart-button, .shopify-payment-button, shopify-accelerated-checkout, form[data-type="add-to-cart-form"]',
    );
    if (!gatilho || gatilho.closest('quick-add-component')) return;
    const escopo = escopoDe(gatilho);
    if (!escopo) return;
    const campo = campoNumeracao(escopo);
    if (!campo) return;
    if (pendente(escopo)) {
      evento.preventDefault();
      evento.stopImmediatePropagation();
      mensagemNumero(campo, true);
      campo.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const primeiro = campo.querySelector('input[data-option-available="true"]');
      primeiro?.focus({ preventScroll: true });
      return;
    }
    if (evento.type === 'click' && gatilho.matches('button[name="add"], .add-to-cart-button')) {
      const marcada = marcadaEm(campo);
      try {
        localStorage.setItem(CHAVE_NUMERO, JSON.stringify({ marca: marcaDo(escopo), numeracao: marcada.value }));
      } catch (e) {
        /* armazenamento opcional */
      }
      window.dataLayer?.push({
        event: 'add_to_cart',
        origem: noModal(escopo) ? 'quick_add' : 'pdp',
        handle: escopo.querySelector('variant-picker')?.dataset.productHandle,
        numeracao: marcada.value,
      });
    }
  }
  document.addEventListener('click', barrarSemNumero, true);
  document.addEventListener('submit', barrarSemNumero, true);

  // "Comprar rápido" em texto no celular: abre o mesmo quick add nativo do card
  document.addEventListener('click', (evento) => {
    const botao = evento.target instanceof Element && evento.target.closest('[data-pc-comprar-rapido]');
    if (!botao) return;
    evento.preventDefault();
    evento.stopPropagation();
    const card = botao.closest('product-card');
    const nativo = card?.querySelector('quick-add-component .quick-add__button--choose, quick-add-component .quick-add__button');
    if (nativo) nativo.click();
    else {
      const link = card?.querySelector('a.product-card__link');
      if (link) window.location.href = link.href;
    }
  });

  new MutationObserver(agendarNumeracao).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', agendarNumeracao);
  else agendarNumeracao();

  window.PaceCerto = {
    cfg,
    limite: LIMITE,
    comparar,
    atualizarBarra: renderBarra,
    slug,
    semAcento,
    nomeCurto,
    ICONE_OK,
    catalogo,
    formatMoeda,
    formatRitmo,
    paraRitmo,
    escapar,
    reguaHTML,
    avisar,
    handlesDeParametro,
    parametroDeHandles,
    lerVistos,
    registrarVisto,
    ICONE_COMPARAR: ICONE,
  };
  document.dispatchEvent(new CustomEvent('pc:pronto'));
})();
